import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useVersionDisplay } from '@julian-querido/version-info';
import { trackNavigation, trackExternalLink } from '../utils/analytics';

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const versionDisplay = useVersionDisplay();

  const handleNavigationClick = (linkText: string, linkUrl: string) => {
    trackNavigation(linkText, linkUrl, 'footer');
  };

  const handleExternalLinkClick = (linkText: string, linkUrl: string) => {
    trackExternalLink(linkText, linkUrl, 'footer');
  };

  const linkClass =
    'text-gray-600 dark:text-gray-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors text-sm';

  return (
    <footer className="border-t border-gray-200/60 dark:border-gray-800/60 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
      <div className="container mx-auto max-w-6xl px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Quick Links */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-4">
              {t('footer.links.title')}
            </h2>
            <ul className="space-y-2.5">
              <li>
                <Link to="/about" className={linkClass} onClick={() => handleNavigationClick(t('nav.about'), '/about')}>
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/projects" className={linkClass} onClick={() => handleNavigationClick(t('nav.projects'), '/projects')}>
                  {t('nav.projects')}
                </Link>
              </li>
              <li>
                <Link to="/skills" className={linkClass} onClick={() => handleNavigationClick(t('nav.skills'), '/skills')}>
                  {t('nav.skills')}
                </Link>
              </li>
              <li>
                <Link to="/testimonials" className={linkClass} onClick={() => handleNavigationClick(t('nav.testimonials'), '/testimonials')}>
                  {t('nav.testimonials')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className={linkClass} onClick={() => handleNavigationClick(t('nav.contact'), '/contact')}>
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className={linkClass} onClick={() => handleNavigationClick(t('footer.links.privacy'), '/privacy')}>
                  {t('footer.links.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className={linkClass} onClick={() => handleNavigationClick(t('footer.links.cookies'), '/cookies')}>
                  {t('footer.links.cookies')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Links */}
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-4">
              {t('footer.social.title')}
            </h2>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="https://github.com/jsaq007"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                  onClick={() => handleExternalLinkClick('GitHub', 'https://github.com/jsaq007')}
                >
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href="https://linkedin.com/in/julian-querido"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                  onClick={() => handleExternalLinkClick('LinkedIn', 'https://linkedin.com/in/julian-querido')}
                >
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright and Version */}
        <div className="mt-10 pt-8 border-t border-gray-200/60 dark:border-gray-800/60">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
              {versionDisplay}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
